"use client";

import * as React from "react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import {
  History,
  Heart,
  MapPin,
  Trash2,
  ChevronDown,
  Building2,
  Activity,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useRightTabs } from "@/contexts/right-tabs-context";
import { MessageNotificationBadge } from "@/components/messaging/MessageNotificationBadge";
import { useMessaging } from "@/contexts/messaging-context";
import type { ListingSearchResult } from "@/app/actions/listing-search";
import Link from "next/link";

interface ListingItem {
  id: string;
  listing: ListingSearchResult;
  viewedAt?: Date;
}

/**
 * MobileHistorySheet - Bottom/Side sheet for History and Likes on mobile/tablet
 *
 * Provides full access to history and liked properties on smaller screens.
 * Uses bottom sheet pattern on mobile for thumb-friendly interaction.
 *
 * UX Considerations:
 * - Tabbed interface prevents cognitive overload
 * - Empty states guide users on how to use the feature
 * - Quick actions available inline
 * - Smooth animations for polished feel
 * - Proper accessibility with ARIA labels
 *
 * @author Ellis Reed
 */
export function MobileHistorySheet() {
  const router = useRouter();
  const pathname = usePathname();
  const { openModal: openMessaging } = useMessaging();
  const {
    history,
    liked,
    isMobileSheetOpen,
    setMobileSheetOpen,
    setActiveListing,
    clearHistory,
    clearLiked
  } = useRightTabs();

  const [activeTab, setActiveTab] = React.useState<string>("history");
  const isDashboard = pathname === "/dashboard";

  // Hide mobile history sheet on admin pages (admin has its own layout)
  const isAdminPage = pathname?.startsWith("/admin");
  if (isAdminPage) {
    return null;
  }

  // Handle property selection
  const handleItemSelect = (id: string) => {
    setActiveListing(id);
    setMobileSheetOpen(false);
    // Navigate to search with modal
    router.push(`/search?modal=${id}`);
  };

  // Render a property item
  const renderPropertyItem = (item: ListingItem, isLiked: boolean = false) => {
    const thumbnailUrl = item.listing.images?.[0] || null;

    return (
      <button
        key={item.id}
        onClick={() => handleItemSelect(item.id)}
        className={cn(
          "w-full flex items-center gap-3 rounded-lg p-3 text-left",
          "transition-all duration-200",
          "hover:bg-accent active:scale-[0.98]",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        )}
      >
        {/* Thumbnail */}
        <div className="relative w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-muted">
          {thumbnailUrl ? (
            <Image
              src={thumbnailUrl}
              alt={item.listing.title}
              fill
              className="object-cover"
              sizes="56px"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <MapPin className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          {isLiked && (
            <div className="absolute bottom-0 right-0 bg-red-500 rounded-tl p-0.5">
              <Heart className="h-3 w-3 text-white fill-current" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate">
            {item.listing.title}
          </h4>
          <p className="text-sm text-muted-foreground">
            ${item.listing.price.toLocaleString()}
          </p>
          {item.listing.city && item.listing.state && (
            <p className="text-xs text-muted-foreground truncate">
              {item.listing.city}, {item.listing.state}
            </p>
          )}
        </div>

        {/* Arrow indicator */}
        <ChevronDown className="h-4 w-4 text-muted-foreground -rotate-90 shrink-0" />
      </button>
    );
  };

  // Empty state component
  const EmptyState = ({
    icon: Icon,
    title,
    description
  }: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
  }) => (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      <h4 className="font-medium mb-1">{title}</h4>
      <p className="text-sm text-muted-foreground max-w-[240px]">
        {description}
      </p>
    </div>
  );

  // Dashboard-specific content
  const renderDashboardContent = () => (
    <div className="flex flex-col gap-4 p-4">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">History</span>
            <History className="h-4 w-4 text-blue-500" />
          </div>
          <p className="text-2xl font-bold" suppressHydrationWarning>
            {history.length}
          </p>
          <p className="text-xs text-muted-foreground">Recently viewed</p>
        </div>
        <div className="rounded-lg p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Liked</span>
            <Heart className="h-4 w-4 text-red-500 fill-red-500" />
          </div>
          <p className="text-2xl font-bold" suppressHydrationWarning>
            {liked.length}
          </p>
          <p className="text-xs text-muted-foreground">Saved favorites</p>
        </div>
      </div>

      <Separator />

      {/* Quick Actions */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4 text-blue-500" />
          Quick Actions
        </h4>
        <div className="space-y-1">
          <Link
            href="/user/listings"
            onClick={() => setMobileSheetOpen(false)}
            className="flex items-center gap-3 rounded-lg p-3 hover:bg-accent transition-colors"
          >
            <Building2 className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-sm font-medium">Manage Listings</p>
              <p className="text-xs text-muted-foreground">Create and edit your properties</p>
            </div>
          </Link>
          <Link
            href="/search"
            onClick={() => setMobileSheetOpen(false)}
            className="flex items-center gap-3 rounded-lg p-3 hover:bg-accent transition-colors"
          >
            <MapPin className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-sm font-medium">Browse Properties</p>
              <p className="text-xs text-muted-foreground">Explore available listings</p>
            </div>
          </Link>
          <button
            onClick={() => {
              setMobileSheetOpen(false);
              openMessaging();
            }}
            className="w-full flex items-center gap-3 rounded-lg p-3 hover:bg-accent transition-colors text-left"
          >
            <MessageNotificationBadge />
            <div>
              <p className="text-sm font-medium">Messages</p>
              <p className="text-xs text-muted-foreground">Check your conversations</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Sheet open={isMobileSheetOpen} onOpenChange={setMobileSheetOpen}>
      <SheetContent
        side="bottom"
        className={cn(
          // Only show on mobile/tablet
          "lg:hidden",
          // Height - adjustable with snap points concept
          "h-[85dvh] sm:h-[70dvh]",
          // Remove default padding
          "p-0",
          // Rounded top corners
          "rounded-t-xl",
          // Custom animation
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom"
        )}
      >
        {/* Drag handle indicator */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 rounded-full bg-muted-foreground/20" />
        </div>

        <SheetHeader className="px-4 pb-2">
          <SheetTitle className="text-left">
            {isDashboard ? "Dashboard" : "Saved Properties"}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Access your property history and liked properties
          </SheetDescription>
        </SheetHeader>

        {isDashboard ? (
          <ScrollArea className="flex-1 h-[calc(85vh-80px)] sm:h-[calc(70vh-80px)]">
            {renderDashboardContent()}
          </ScrollArea>
        ) : (
          <>
            {/* Tabs for History/Liked */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex flex-col flex-1"
            >
              <TabsList className="mx-4 grid grid-cols-2">
                <TabsTrigger
                  value="history"
                  className="flex items-center gap-2"
                >
                  <History className="h-4 w-4" />
                  <span>History</span>
                  {history.length > 0 && (
                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
                      {history.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="liked" className="flex items-center gap-2">
                  <Heart className="h-4 w-4" />
                  <span>Liked</span>
                  {liked.length > 0 && (
                    <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded-full">
                      {liked.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* History Tab Content */}
              <TabsContent
                value="history"
                className="flex-1 m-0 outline-none"
              >
                <ScrollArea className="h-[calc(85vh-160px)] sm:h-[calc(70vh-160px)]">
                  {history.length === 0 ? (
                    <EmptyState
                      icon={History}
                      title="No viewing history"
                      description="Properties you view will appear here for quick access"
                    />
                  ) : (
                    <div className="p-4 space-y-1">
                      {/* Clear button */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-muted-foreground">
                          {history.length} of 10 max
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearHistory}
                          className="h-8 text-xs text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Clear all
                        </Button>
                      </div>
                      {history.map((item) => renderPropertyItem(item, false))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Liked Tab Content */}
              <TabsContent value="liked" className="flex-1 m-0 outline-none">
                <ScrollArea className="h-[calc(85vh-160px)] sm:h-[calc(70vh-160px)]">
                  {liked.length === 0 ? (
                    <EmptyState
                      icon={Heart}
                      title="No liked properties"
                      description="Tap the heart icon on any property to save it here"
                    />
                  ) : (
                    <div className="p-4 space-y-1">
                      {/* Clear button */}
                      <div className="flex justify-end mb-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearLiked}
                          className="h-8 text-xs text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Clear all
                        </Button>
                      </div>
                      {liked.map((item) => renderPropertyItem(item, true))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Messages quick access - always visible */}
        <div className="border-t p-4 bg-background">
          <button
            onClick={() => {
              setMobileSheetOpen(false);
              openMessaging();
            }}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-accent transition-colors"
          >
            <MessageNotificationBadge />
            <span className="text-sm font-medium">Messages</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
